import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { asTextArray, buildLearningTopicPath } from '../../lib/educationContent';
import { trackLearningEvent } from '../../lib/learningEvents';
import LearningFeedbackButtons from './LearningFeedbackButtons';
import LearningList from './LearningList';

const ContextualLearningPanel = ({ topics, questionKey, matterId, userId }) => {
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const primary = topics?.[0];

  if (!primary?.topic) return null;

  const topicRows = topics.filter((row) => row.topic);
  const expandedRow = topicRows.find((row) => row.topic.id === expandedTopicId);

  const trackExpansion = async (row) => {
    const topic = row?.topic;
    if (!topic?.id || !userId) return;

    try {
      await trackLearningEvent({
        userId,
        matterId,
        questionKey,
        topicId: topic.id,
        eventType: 'topic_expanded',
        metadata: {
          source: 'guided_intake',
          topic_slug: topic.slug
        }
      });
    } catch (err) {
      console.warn('Unable to record learning event:', err);
    }
  };

  const toggleExpanded = (row) => {
    const topicId = row.topic.id;
    const nextTopicId = expandedTopicId === topicId ? null : topicId;
    setExpandedTopicId(nextTopicId);
    if (nextTopicId) trackExpansion(row);
  };

  const misconceptions = asTextArray(expandedRow?.topic?.common_misconceptions_json);
  const examples = asTextArray(expandedRow?.topic?.examples_json);

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-blue-600">
          <SafeIcon icon={FiIcons.FiBookOpen} className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Why we're asking</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">
            {primary.immediate_context || primary.topic.why_we_are_asking || primary.topic.short_summary}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {topicRows.map((row) => (
              <button
                key={row.topic.id}
                type="button"
                onClick={() => toggleExpanded(row)}
                className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                  expandedTopicId === row.topic.id
                    ? 'border-blue-200 bg-white text-blue-700'
                    : 'border-transparent bg-white/70 text-slate-600 hover:bg-white'
                }`}
              >
                {row.context_label || row.topic.title}
                <SafeIcon
                  icon={expandedTopicId === row.topic.id ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
                  className="ml-1.5 h-3.5 w-3.5"
                />
              </button>
            ))}
          </div>

          {expandedRow && (
            <div className="mt-4 space-y-4 rounded-lg border border-blue-100 bg-white p-4">
              <div>
                <h4 className="text-sm font-bold text-slate-900">{expandedRow.topic.title}</h4>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  {expandedRow.topic.plain_english_explanation || expandedRow.topic.short_summary}
                </p>
              </div>

              {expandedRow.topic.why_this_matters && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Why this matters</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{expandedRow.topic.why_this_matters}</p>
                </div>
              )}

              {misconceptions.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Common misconceptions</p>
                  <div className="mt-1 text-sm text-slate-600">
                    <LearningList items={misconceptions} prefix="Misconception:" limit={3} />
                  </div>
                </div>
              )}

              {examples.length > 0 && (
                <div className="rounded-md bg-slate-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Example</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{examples[0]}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <Link
                  to={buildLearningTopicPath({ slug: expandedRow.topic.slug, matterId, questionKey })}
                  className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 hover:bg-blue-100"
                >
                  <SafeIcon icon={FiIcons.FiExternalLink} className="mr-1.5 h-3.5 w-3.5" />
                  Learn more
                </Link>
                <LearningFeedbackButtons
                  topic={expandedRow.topic}
                  matterId={matterId}
                  questionKey={questionKey}
                  userId={userId}
                  source="guided_intake"
                  compact
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextualLearningPanel;
